import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Buffer } from "buffer";

import { ToastrService } from "ngx-toastr";
import { api } from "../../utils/endpoints";
import { AuthPermissionService } from "./auth-permission.service";
import { CommonDataSharingService } from "./common-data-sharing.service";
import { ApiCallsService } from "./network/api-calls.service";
@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly _secretKey: string = "armstracker-web-encrytion";

  constructor(
    private _router: Router,
    private _apiCalls: ApiCallsService,
    private _authPermissions: AuthPermissionService,
    private _toastr: ToastrService,
    private _commonDataService: CommonDataSharingService
  ) {}

  login(request: any) {
    return new Promise((resolve, reject) => {
      const encryptPayload: any = this._commonDataService.encryptData(request); // encrypt payload request

      this._apiCalls
        .postDataWithoutLoader(api._adminLogin, encryptPayload)
        .subscribe({
          next: (res: any) => {
            const decryptResponse: any =
              this._commonDataService.decryptData(res); // Decrypt api response
            this._router.navigate([
              "/auth/two-factor-authorization/" + decryptResponse.data.userId,
            ]);
          },
          error: (error: any) => {
            console.error(error, "----apiError");
            reject();
          },
          complete: () => {
            console.log("API call completed");
          },
        });
    });
  }

  twoFactorAuth(request: any) {
    return new Promise((resolve, reject) => {
      const encryptPayload = this._commonDataService.encryptData(request); // encrypt payload request

      this._apiCalls
        .postDataWithoutLoader(api._requestOtp, encryptPayload)
        .subscribe({
          next: (res: any) => {
            this._commonDataService.decryptData(res);
            const decryptResponse: any =
              this._commonDataService.decryptData(res); // Decrypt api response

            if (request.otpMedium == "mobile") {
              this._router.navigate(["/auth/verify-otp/" + request.userId], {
                queryParams: {
                  verificationMethod: "mobile",
                  value: decryptResponse.data.mobile,
                },
              });
            } else {
              this._router.navigate(["/auth/verify-otp/" + request.userId], {
                queryParams: {
                  verificationMethod: "email",
                  value: decryptResponse.data.email,
                },
              });
            }
          },
          error: (error: any) => reject(),
        });
    });
  }

  verifyOtp(request: any) {
    return new Promise((resolve, reject) => {
      const encryptPayload = this._commonDataService.encryptData(request); // encrypt payload request

      this._apiCalls
        .postDataWithoutLoader(api._verifyOtp, encryptPayload)
        .subscribe({
          next: (res: any) => {
            this._commonDataService.decryptData(res);
            const decryptResponse: any =
              this._commonDataService.decryptData(res); // Decrypt api response
            this._saveAdminDetails(decryptResponse.data);
            this._navigateToAgencySelection(decryptResponse.data.token);
          },
          error: (error: any) => reject(),
        });
    });
  }

  resendOtp(request: any) {
    return new Promise((resolve, reject) => {
      const encryptPayload = this._commonDataService.encryptData(request); // encrypt payload request

      this._apiCalls
        .postDataWithoutLoader(api._requestOtp, encryptPayload)
        .subscribe({
          next: (res: any) => {
            this._commonDataService.decryptData(res);
            const decryptResponse: any =
              this._commonDataService.decryptData(res); // Decrypt api response
            const verificationMethod: any =
              request.otpMedium == "mobile" ? "mobile" : "email";
            this._toastr.success(
              "OTP has been sent to your " + verificationMethod
            );
            resolve(decryptResponse);
          },
          error: (error: any) => reject(),
        });
    });
  }

  signOut(): void {
    this._apiCalls.post(api._logoOut, {}).subscribe({
      next: (res: any) => {
        this._clearLocalStorage();
      },
      error: (error: any) => console.log(error),
    });
  }

  saveUserCredentials(loginDetails: any): void {
    const encryptedDetails = CryptoJS.AES.encrypt(
      JSON.stringify(loginDetails),
      this._secretKey
    ).toString();
    localStorage.setItem("sessionDetails", encryptedDetails);
  }

  private _clearSessionDetails(): void {
    localStorage.removeItem("sessionDetails");
  }

  private _navigateToAgencySelection(token: any) {
    this._saveAuthToken(token);
    this._router.navigate(["/auth/agency"]);
  }

  private _saveAuthToken(token: any): void {
    localStorage.setItem("adminToken", Buffer.from(token).toString("base64"));
  }

  private _saveAdminDetails(res: any): void {
    const adminDetails = JSON.stringify(res);
    localStorage.setItem(
      "adminDetails",
      Buffer.from(adminDetails).toString("base64")
    );
  }

  _saveSelectedAgency(agency: any): void {
    localStorage.setItem(
      "selectedAgency",
      Buffer.from(agency).toString("base64")
    );
  }

  getAdminInfo() {
    const adminDetail = localStorage.getItem("adminDetails");
    if (adminDetail) return JSON.parse(atob(adminDetail || ""));
  }

  _clearLocalStorage(): void {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminDetails");
    localStorage.removeItem("selectedAgency");
    this._router.navigate(["/auth/login"]);
  }

  get isAuthenticated(): boolean {
    const token = localStorage.getItem("adminToken");
    if (token) return true;
    return false;
  }

  get credentials(): any {
    const credentials = localStorage.getItem("sessionDetails");
    if (credentials) {
      const decryptedBytes = CryptoJS.AES.decrypt(credentials, this._secretKey);
      const decryptedMessage = JSON.parse(
        decryptedBytes.toString(CryptoJS.enc.Utf8)
      );
      return decryptedMessage;
    }
    return null;
  }

  authenticatedUserRouting(): boolean {
    const token = localStorage.getItem("adminToken");
    if (token) return true;
    this._clearLocalStorage();
    return false;
  }

  anonymousRouting(): boolean {
    const token = localStorage.getItem("adminToken");
    const selectedAgency = atob(localStorage.getItem("selectedAgency") || "");
    if (token) {
      this._router.navigate(["/form-builder"], {
        queryParams: { selectedAgency: selectedAgency },
      });
      return false;
    }
    return true;
  }
}
